/**
 * CollaborationBoard — operator-facing view of a multi-employee Conversation
 * (DIGEMP-MULTI-EMPLOYEE-COLLABORATION-FRONTEND-059).
 *
 * Composes the existing conversationsApi.get / listMessages endpoints only — no new backend call,
 * no change to assignment authority. Shows, in plain Hebrew: who took part, who is responsible
 * right now, what each employee reported, the handoffs between them and the single final
 * human-facing response. Raw run logs / tool payloads / provider output are deliberately absent —
 * the gated technical details stay in ConversationPanel's collapsed trace box.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { conversationsApi } from "../services/api";
import EmployeeAvatar from "./EmployeeAvatar";
import { conversationStatusBadgeClass, conversationStatusLabel } from "./conversationStatusVocabulary";

const STAGE_LABELS = {
  pending: "ממתין להתחלה", analyzing: "מנתח", diagnosing: "מאבחן", executing: "מבצע", approved: "אושר",
  awaiting_technician_review: "ממתין לסקירת טכנאי", awaiting_approval: "ממתין לאישור",
  completed: "הושלם", resolved: "נפתר", failed: "נכשל", rejected: "נדחה", paused: "מושהה",
};

const TRACE_STATUS_LABELS = {
  SUCCEEDED: "הושלם",
  FAILED: "נכשל",
  PROPOSED: "ממתין לביצוע",
  APPROVED: "אושר",
};

const TERMINAL_STAGES = new Set(["completed", "resolved", "failed", "rejected"]);
const AWAITING_STAGES = new Set(["awaiting_technician_review", "awaiting_approval"]);

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("he-IL");
  } catch {
    return "";
  }
}

function lastOf(messages) {
  return messages.reduce((acc, m) => (acc === null || m.sequence_no > acc.sequence_no ? m : acc), null);
}

function stageChipClass(stage) {
  if (stage === "failed" || stage === "rejected") return "bg-red-100 text-red-700";
  if (TERMINAL_STAGES.has(stage)) return "bg-green-100 text-green-700";
  if (AWAITING_STAGES.has(stage)) return "bg-amber-100 text-amber-700";
  return "bg-violet-100 text-violet-700";
}

function employeeName(participant) {
  return participant.agent_name || "עובד לא מזוהה";
}

function AuthorBadge({ author }) {
  if (author?.role === "agent" && author.agent_name) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <EmployeeAvatar
          name={author.agent_name}
          avatarKey={author.agent_avatar_key}
          avatarColor={author.agent_avatar_color}
          size="sm"
        />
        <span className="font-semibold">{author.agent_name}</span>
        {author.agent_role_title && <span className="text-violet-500">· {author.agent_role_title}</span>}
      </span>
    );
  }
  if (author?.role === "manager") {
    return <span className="font-semibold">🧭 מנהל הצוות</span>;
  }
  return <span className="font-semibold">🤖 סוכן AI</span>;
}

export default function CollaborationBoard({ conversationId }) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["collaboration-board", conversationId],
    queryFn: async () => {
      const [conversation, messages] = await Promise.all([
        conversationsApi.get(conversationId),
        conversationsApi.listMessages(conversationId),
      ]);
      return { conversation, messages };
    },
    enabled: Boolean(conversationId),
    refetchInterval: 15000,
  });

  if (isLoading) return <p className="text-gray-400 text-sm">טוען...</p>;

  if (isError) {
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
        {error?.response?.data?.detail || "שגיאה בטעינת תצוגת שיתוף הפעולה"}
        <button type="button" onClick={() => refetch()} className="mr-2 underline">נסה שוב</button>
      </div>
    );
  }

  const conversation = data?.conversation || {};
  const messages = data?.messages || [];
  const participants = conversation.participants || [];
  const manager = participants.find((p) => p.role === "manager") || null;
  const employees = participants.filter((p) => p.role === "agent");
  const activeEmployees = employees.filter((p) => p.is_active);

  const byAuthor = new Map();
  for (const m of messages) {
    if (!m.author_participant_id) continue;
    if (!byAuthor.has(m.author_participant_id)) byAuthor.set(m.author_participant_id, []);
    byAuthor.get(m.author_participant_id).push(m);
  }

  const handoffs = messages.filter((m) => m.message_type === "handoff_notice");
  const finalResponse = lastOf(messages.filter((m) => m.message_type === "human_response"));
  const finalAuthor = finalResponse
    ? participants.find((p) => p.id === finalResponse.author_participant_id) || null
    : null;

  const responsibilityText = employees.length === 0
    ? "אין עובדים בשיחה זו"
    : activeEmployees.length > 0
      ? `אחריות נוכחית: ${activeEmployees.map(employeeName).join(" ו-")}`
      : conversation.status === "closed"
        ? "השיחה סגורה — אין עובד/ת פעיל/ה"
        : "אין עובד/ת פעיל/ה כרגע";

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm">
            {conversation.ticket_id ? (
              <Link to={`/tickets/${conversation.ticket_id}?tab=ai-chat`} className="text-blue-600 hover:underline">
                קריאה #{String(conversation.ticket_id).slice(0, 8)}
              </Link>
            ) : (
              <span className="text-gray-400 italic">ללא קריאה</span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full ${conversationStatusBadgeClass(conversation.status)}`}>
              {conversationStatusLabel(conversation.status)}
            </span>
          </div>
          <span className="text-xs text-gray-400">{formatTime(conversation.updated_at)}</span>
        </div>
      </div>

      <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
        <h2 className="text-sm font-semibold text-violet-800 mb-2">המענה האחרון לאדם</h2>
        {finalResponse ? (
          <>
            <div className="text-xs text-violet-700 mb-1">
              <AuthorBadge author={finalAuthor} />
            </div>
            <div className="text-sm text-violet-900 whitespace-pre-wrap leading-relaxed">{finalResponse.content}</div>
            <div className="text-xs text-violet-400 mt-2">{formatTime(finalResponse.created_at)}</div>
          </>
        ) : (
          <p className="text-sm text-violet-500 italic">טרם הוצג מענה לאדם בשיחה זו.</p>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-1">צוות השיחה</h2>
        <p className="text-xs text-gray-500 mb-3">{responsibilityText}</p>

        {employees.length === 0 && (
          <p className="text-sm text-gray-400 italic">לא הצטרפו עובדים לשיחה זו.</p>
        )}

        {manager && (
          <div className="flex items-center gap-2 text-sm text-gray-700 border border-gray-100 rounded-lg px-3 py-2 mb-2">
            <span aria-hidden="true">🧭</span>
            <span className="font-medium">מנהל הצוות</span>
            <span className="text-xs text-gray-400">— ניתוב הפניות וחלוקת העבודה</span>
          </div>
        )}

        <ul className="space-y-2">
          {employees.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-2 border border-gray-100 rounded-lg px-3 py-2">
              <EmployeeAvatar
                name={employeeName(p)}
                avatarKey={p.agent_avatar_key}
                avatarColor={p.agent_avatar_color}
                status={p.agent_availability_status}
                isEnabled={p.agent_is_enabled}
                size="sm"
                showStatus
              />
              <span className="text-sm font-medium text-gray-800">{employeeName(p)}</span>
              {p.agent_role_title && <span className="text-xs text-gray-500">· {p.agent_role_title}</span>}
              <span className="text-xs text-gray-400">
                {p.left_at
                  ? `עזב/ה את השיחה · ${formatTime(p.left_at)}`
                  : p.is_active
                    ? "פעיל/ה בשיחה"
                    : "לא פעיל/ה"}
              </span>
              {p.handoff_reason && (
                <span className="text-xs text-violet-600 basis-full">סיבת הצטרפות: {p.handoff_reason}</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">ממצאי העובדים</h2>
        {employees.length === 0 && (
          <p className="text-sm text-gray-400 italic">אין עובדים להצגת ממצאים.</p>
        )}
        <ul className="space-y-3">
          {employees.map((p) => {
            const mine = byAuthor.get(p.id) || [];
            const finding = lastOf(mine.filter((m) => m.message_type === "human_response"));
            const trace = lastOf(mine.filter((m) => m.message_type === "technical_trace"));
            const stage = trace?.technical_trace?.run_stage || null;
            const stageLabel = trace
              ? (trace.technical_trace?.run_stage_label
                || STAGE_LABELS[stage]
                || TRACE_STATUS_LABELS[trace.technical_trace?.action_status]
                || null)
              : null;
            return (
              <li key={p.id} className="border border-gray-100 rounded-lg px-3 py-2">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-800">{employeeName(p)}</span>
                  {stageLabel && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${stageChipClass(stage)}`}>{stageLabel}</span>
                  )}
                  {trace?.agent_run_id && (
                    <Link to={`/agent-runs/${trace.agent_run_id}`} className="text-xs text-blue-600 hover:underline">
                      פתח משימה
                    </Link>
                  )}
                </div>
                {finding ? (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{finding.content}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic">טרם דווח ממצא.</p>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">העברות בשיחה</h2>
        {handoffs.length === 0 ? (
          <p className="text-sm text-gray-400 italic">לא נרשמו העברות בשיחה זו.</p>
        ) : (
          <ol className="space-y-2">
            {handoffs.map((h) => (
              <li key={h.id} className="flex flex-wrap items-baseline gap-2 text-sm text-gray-700">
                <span aria-hidden="true">↪</span>
                <span className="flex-1">{h.content}</span>
                <span className="text-xs text-gray-400">{formatTime(h.created_at)}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
